import { isValidEmail, isValidUsPhone, parseDealValueCents } from './private-event-fields';
import type { PrivateEventFormFields as Fields } from './private-event-form-state';

/** Field key → message, keyed the way the server keys the same errors. */
export type StepErrors = Record<string, string>;

/** The server's integer ceiling for headcount and deal value in cents. */
const MAX_INT = 2_147_483_647;

export const END_AFTER_START_MESSAGE = 'End time must be after the start time.';

function addIfBlank(errs: StepErrors, key: string, value: string, message: string) {
  if (value.trim() === '') errs[key] = message;
}

function clientAddressErrors(f: Fields): StepErrors {
  const errs: StepErrors = {};
  addIfBlank(errs, 'locationName', f.locationName, 'Enter the location name.');
  addIfBlank(errs, 'addressLine1', f.addressLine1, 'Enter the street address.');
  addIfBlank(errs, 'city', f.city, 'Enter the city.');
  addIfBlank(errs, 'state', f.state, 'Select a state.');
  addIfBlank(errs, 'zipCode', f.zipCode, 'Enter the ZIP code.');
  return errs;
}

function locationErrors(f: Fields): StepErrors {
  const errs: StepErrors = {};
  addIfBlank(errs, 'regionId', f.regionId, 'Select a region.');
  if (f.locationMode === 'venue') {
    addIfBlank(errs, 'venueId', f.venueId, 'Select a venue.');
    return errs;
  }
  if (f.locationMode === 'client-site') Object.assign(errs, clientAddressErrors(f));
  addIfBlank(errs, 'timeZone', f.timeZone, 'Select a time zone.');
  return errs;
}

function dateErrors(f: Fields, today: string): StepErrors {
  const errs: StepErrors = {};
  if (!f.date) errs.startsAt = 'Pick a date.';
  else if (f.date < today) errs.startsAt = 'That date has already passed.';
  addIfBlank(errs, 'startTime', f.startTime, 'Enter a start time.');
  addIfBlank(errs, 'endTime', f.endTime, 'Enter an end time.');
  if (f.startTime && f.endTime && f.endTime <= f.startTime) errs.endsAt = END_AFTER_START_MESSAGE;
  return errs;
}

function whenWhereErrors(f: Fields, today: string): StepErrors {
  const errs: StepErrors = { ...locationErrors(f), ...dateErrors(f, today) };
  addIfBlank(errs, 'workshopTypeId', f.workshopTypeId, 'Select a workshop type.');
  return errs;
}

export function headcountError(value: string): string | null {
  if (value.trim() === '') return 'Enter the number of guests.';
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return 'Use a whole number, 0 or more.';
  return n > MAX_INT ? 'That headcount is too large.' : null;
}

function clientErrors(f: Fields): StepErrors {
  const errs: StepErrors = {};
  addIfBlank(errs, 'clientName', f.clientName, 'Enter the client name.');
  const headcount = headcountError(f.headcount);
  if (headcount) errs.headcount = headcount;
  const email = f.contactEmail.trim();
  if (email && !isValidEmail(email)) errs.contactEmail = 'Enter a valid email address.';
  const phone = f.contactPhone.trim();
  if (phone && !isValidUsPhone(phone)) {
    errs.contactPhone = 'Enter a US phone number, like (555) 123-4567.';
  }
  return errs;
}

function dealErrors(f: Fields): StepErrors {
  const cents = parseDealValueCents(f.dealValue);
  if (cents === undefined) return { dealValueCents: 'Enter an amount, like 2,400.' };
  if (cents !== null && cents > MAX_INT) return { dealValueCents: 'That amount is too large.' };
  return {};
}

/** The errors for step `step` (0 when and where, 1 client, 2 deal), {} when
 *  valid. `today` is the calendar date in the event's zone. */
export function errorsForStep(step: number, fields: Fields, today: string): StepErrors {
  if (step === 0) return whenWhereErrors(fields, today);
  if (step === 1) return clientErrors(fields);
  return dealErrors(fields);
}
